import { Component, OnDestroy, OnInit } from '@angular/core';

import { faFileCsv as falFileCsv } from '@fortawesome/pro-light-svg-icons/faFileCsv';
import { AbstractPage } from '../abstract-page';
import { forEachCombination } from '../../utils/objects';
import { Stringifiable } from '../../utils/types';
import { replaceVariablesInUrl } from '../../utils/url';
import { environment } from '../../../environments/environment';
import { enumToArray } from '../../utils/arrays';
import { BarometerPeriodEnum } from '../../models/domain/barometer-period-enum';
import { TranslationHelperService } from '../../services/translations/translation-helper.service';
import { RGBColor } from '../../utils/RGBColor';
import { ProjectService } from '../../services/project.service';
import { MetadataService } from '../../services/metadata.service';
import { catchError, forkJoin, interval, map, of, startWith, Subscription, switchMap, tap } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { OpenDataService } from 'src/app/services/opendate.service';

export type OpenDataProjects = Array<{
    title: string;
    badges: Array<Badge>;
    description?: string;
    license?: string;
    prefix?: string;
    variables?: { [name: string]: Array<Stringifiable> };
    urls?: Array<{ label: string; value: string }>;
}>;

export interface Badge {
    label: string;
    colors: {
        text: RGBColor;
        background: RGBColor;
    };
}

export const BADGE_COLORS_BY_TYPE = {
    filetype: {
        text: new RGBColor(255, 255, 255),
        background: new RGBColor(199, 52, 62),
    },
    service: {
        text: new RGBColor(255, 255, 255),
        background: new RGBColor(133, 77, 148),
    },
};

@Component({
    selector: 'app-opendata-page',
    templateUrl: './opendata-page.component.html',
    styleUrls: ['./opendata-page.component.scss'],
})
export class OpendataPageComponent implements OnInit, OnDestroy {
    subscriptions: Array<Subscription> = [];
    readonly icons = {
        falFileCsv,
    };

    projects: OpenDataProjects = [
        // {
        //   title: 'opendata.atlasMobileGrid.title',
        //   description: 'opendata.atlasMobileGrid.description',
        //   baseUrl: environment.opendata.atlasMobileGrid.url,
        //   variables: {
        //     provider: DOMAIN.providers.map(provider => provider.value),
        //     technology: [TechnologyEnum._3G, TechnologyEnum._4G],
        //   },
        // },
        {
            title: 'opendata.atlasMobileArea.title',
            badges: [
                { label: 'CSV', colors: BADGE_COLORS_BY_TYPE.filetype },
                { label: 'Internet', colors: BADGE_COLORS_BY_TYPE.service },
                { label: 'Mobile', colors: BADGE_COLORS_BY_TYPE.service },
            ],
            description: 'opendata.atlasMobileArea.description',
            // license: '&copy; BIPT',
            prefix: 'AtlasMobile',
        },
        {
            title: 'opendata.atlasFix.title',
            badges: [
                { label: 'CSV', colors: BADGE_COLORS_BY_TYPE.filetype },
                { label: 'Internet', colors: BADGE_COLORS_BY_TYPE.service },
                { label: 'Landline', colors: BADGE_COLORS_BY_TYPE.service },
            ],
            description: 'opendata.atlasFix.description',
            prefix: 'AtlasFixed',
        },
        // {
        //   title: 'opendata.barometer.title',
        //   badges: [
        //     { label: 'CSV', colors: BADGE_COLORS_BY_TYPE.filetype },
        //     { label: 'Internet', colors: BADGE_COLORS_BY_TYPE.service },
        //     { label: 'TV', colors: BADGE_COLORS_BY_TYPE.service },
        //     { label: 'Telephone', colors: BADGE_COLORS_BY_TYPE.service },
        //   ],
        //   description: 'opendata.barometer.description',
        //   // license: '&copy; BIPT',
        //   baseUrl: environment.opendata.barometer.url,
        //   variables: {
        //     period: enumToArray(BarometerPeriodEnum),
        //   },
        // },
        // {
        //   title: 'opendata.driveTesting.title',
        //   description: 'opendata.driveTesting.description',
        //   // baseUrl: environment.opendata.atlasFix.url,
        //   // variables: {
        //   //   level: DOMAIN.areasLevels.map(level => level.value),
        //   // },
        // },
    ];

    constructor(
        protected translationHelper: TranslationHelperService,
        protected projectService: ProjectService,
        protected metadata: MetadataService,
        protected opendataService: OpenDataService
    ) {
        this.metadata.setImage(environment.baseHref + 'assets/images/og/data-portal.png');
        this.metadata.setSubtitle('opendata.title');
        this.metadata.setDescription('opendata.description');
        this.projectService.select(null);
        const group = projectService.groups.filter((c) => c.group == 'Data')[0];
        projectService.selectGroup(group);
    }

    ngOnInit() {
        this.fillProjectsUrls();
    }

    fillProjectsUrls() {
        const observables = this.projects.map((p) => {
            return this.opendataService.getSasUrl(p.prefix);
        });

        const subscription = interval(540000)
            .pipe(
                startWith(0),
                switchMap(() =>
                    forkJoin(observables).pipe(
                        tap((res) => {
                            res.forEach((r, i) => {
                                this.projects[i].urls = [];
                                for (let sas of r) {
                                    let label = sas?.split('/').pop().split('?')[0].split('.')[0];
                                    this.projects[i].urls.push({
                                        label: label,
                                        value: sas,
                                    });
                                }

                                // Sort by date (newest first) and alphabetically for same dates
                                this.projects[i].urls.sort((a, b) => {
                                    const dateA = a.label.match(/(\d{2})-(\d{4})$/);
                                    const dateB = b.label.match(/(\d{2})-(\d{4})$/);

                                    if (dateA && dateB) {
                                        const monthA = parseInt(dateA[1]) - 1;
                                        const yearA = parseInt(dateA[2]);
                                        const monthB = parseInt(dateB[1]) - 1;
                                        const yearB = parseInt(dateB[2]);

                                        const dateComparison = new Date(yearB, monthB).getTime() - new Date(yearA, monthA).getTime();

                                        // If dates are equal, sort alphabetically
                                        if (dateComparison === 0) {
                                            return a.label.localeCompare(b.label);
                                        }

                                        return dateComparison;
                                    }
                                    return 0; // Keep original order if no date pattern
                                });
                            });
                        })
                    )
                )
            )
            .subscribe();

        this.subscriptions.push(subscription);
    }
    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }
}
